<script setup lang="ts">
import t from "~/utils/translate";
const route = useRoute();
import { useBetbook } from "~/store/betbook";
import { IMarket } from "~/types/sports/market";
import ExpandableOutcomeRow from "./ExpandableOutcomeRow.vue";
import OutcomesLoader from "~/components/layout/TrackRacing/Shared/Outcome/OutcomesLoader.vue";
import Results from "~/components/layout/TrackRacing/Shared/Event/Results.vue";
import OutcomeRow from "./OutcomeRow.vue";
import { isMobile } from "~/utils/constants";

interface Props {
	selectedEventId: number | undefined;
	isMultiLayout?: boolean;
	isEventFinished: boolean | undefined;
	isLoading?: boolean;
	isStartingPrice?: boolean;
}
interface IColumnHeaders {
	title: string;
	isHidden?: boolean;
	class?: string;
}

const props = defineProps<Props>();
const store = useBetbook();

const sportId = computed(() => {
	return route.params.sportId;
});

const eventMarkets = computed(() => {
	const markets = [...store.markets.values()].filter((m) => m.shouldDisplay && m.eventId === props.selectedEventId);
	return isMobile ? markets.slice(0, 2) : markets.slice(0, 4);
});

const defaultMarket = computed(() => {
	return eventMarkets.value.find((m) => m.name.includes("Race Winner")) || eventMarkets.value[0];
});

const outcomes = computed(() => {
	return [...store.outcomes.values()]
		.filter(
			(o) =>
				(o.marketId === defaultMarket.value?.marketId && o.shouldDisplay) || (o.marketId === defaultMarket.value?.marketId && !props.isEventFinished)
		)
		.sort((a, b) => Number(b.isTradingActive) - Number(a.isTradingActive));
});

const marketTitle = (market: IMarket) => {
	return market.displayName.toLowerCase().includes("race winner") || market.displayName.toLowerCase().includes("ante post")
		? { title: t("win"), class: "flex justify-center text-center" }
		: {
				title: market.displayName.replace("Horse Racing", "").replace("- ZA", "").replaceAll(" - ", " "),
				class: "flex justify-between text-center",
		  };
};

const outcomeHasAdditionalInfo = computed(() => {
	return outcomes.value.some((outcome) => outcome.additionalInfo && Object.keys(outcome.additionalInfo).length > 0);
});

const outcomeColumnHeaders = computed(() => {
	const headers: IColumnHeaders[] = [
		{ title: t("no."), class: "flex justify-center" },
		{ title: t("silk"), class: "flex justify-center", isHidden: sportId.value === "greyhounds" },
		{ title: t("name"), class: "justify-start" },
		{ title: t("age"), isHidden: isMobile || !outcomeHasAdditionalInfo.value, class: "flex justify-center " },
		{ title: t("weight"), isHidden: isMobile || !outcomeHasAdditionalInfo.value, class: "flex justify-center " },
		{ title: t("form"), isHidden: isMobile || !outcomeHasAdditionalInfo.value, class: "flex justify-center" },
		{ title: t(""), isHidden: isMobile || !outcomeHasAdditionalInfo.value, class: "flex justify-center" },
		...eventMarkets.value.map((market) => marketTitle(market)),
	];
	return headers.filter((h) => !h.isHidden);
});

const marketColumns = computed(() => {
	return eventMarkets.value.map(() => " 55px").join("");
});

const gridClass = computed(() => {
	const gridTemplateCols =
		sportId.value === "greyhounds"
			? `35px 1fr ${marketColumns.value}`
			: isMobile || !outcomeHasAdditionalInfo.value
			? `25px 45px 1fr ${marketColumns.value}`
			: `25px 45px minmax(50px,1fr) repeat(3, minmax(20px, 70px)) minmax(20px, 150px) ${marketColumns.value}`;
	return `grid-template-columns: ${gridTemplateCols}`;
});
</script>

<template>
	<OutcomesLoader v-if="isLoading" />
	<div v-else class="rounded-lg w-full bg-light-50 dark:bg-dark-800">
		<Results :event-id="selectedEventId" v-if="isEventFinished" />
		<div v-if="!isEventFinished && eventMarkets.length > 0 && outcomes.length > 0">
			<div
				:class="[
					'items-center min-h-[34px] bg-dark-800 dark:bg-dark-700 rounded-t-lg text-light-50 grid gap-1 px-1',
					outcomeHasAdditionalInfo || (isMobile && sportId !== 'greyhounds') ? 'pl-9' : null,
				]"
				:style="gridClass"
			>
				<div
					v-for="header of outcomeColumnHeaders"
					:key="header.title"
					class="flex items-center text-xs"
					:class="header.class || 'justify-center text-center'"
				>
					<div class="leading-tight">
						<span class="text-xs font-bold">{{ t(header.title) }}</span>
					</div>
				</div>
			</div>
			<div
				v-for="(outcome, index) in outcomes"
				:key="index"
				:class="index + 1 !== outcomes?.length ? 'border-b border-b-light-400 dark:border-b-dark-500' : 'overflow-hidden'"
			>
				<ExpandableOutcomeRow
					v-if="(isMobile && sportId !== 'greyhounds') || outcomeHasAdditionalInfo"
					:isStartingPrice="isStartingPrice"
					:outcome="outcome"
					:markets="eventMarkets"
					:selected-event-id="selectedEventId"
					:sport-id="sportId.toString()"
				/>
				<OutcomeRow
					v-else
					:isStartingPrice="isStartingPrice"
					:outcome="outcome"
					:markets="eventMarkets"
					:event-id="selectedEventId"
					:show-additional-info="outcomeHasAdditionalInfo"
					:sport-id="sportId.toString()"
				/>
			</div>
		</div>
		<div v-else-if="!isEventFinished" class="w-full rounded-lg bg-light-50 dark:bg-dark-800 text-xs text-center font-bold my-2 py-2">
			{{ t("horses-no-markets-message") }}
		</div>
	</div>
</template>
